/**
 * Shared animation primitives for the DS core (production sections).
 *   - LazyMotion + `m` → ~4.6kb base instead of the full ~34kb `motion` bundle;
 *     `domAnimation` features only. `strict` forbids the heavy `motion.*` API.
 *   - transform/opacity-only reveals (no layout props) → no CLS.
 *   - whileInView + viewport.once → animate once on scroll.
 *   - useReducedMotion → fully disabled when the OS asks for reduced motion.
 */
import { LazyMotion, domAnimation, m, useReducedMotion } from "motion/react";
import type { ReactNode, CSSProperties } from "react";

export function MotionProvider({ children }: { children: ReactNode }) {
  return (
    <LazyMotion features={domAnimation} strict>
      {children}
    </LazyMotion>
  );
}

/** House easing — matches the Framer project's tween feel. */
export const EASE = [0.22, 0.61, 0.36, 1] as const;

type RevealTag = "div" | "section" | "span" | "li" | "h1" | "h2" | "h3" | "p";

type RevealProps = {
  children: ReactNode;
  /** Semantic tag to render (keeps real h1/p in the DOM for SEO). */
  as?: RevealTag;
  delay?: number;
  /** Initial vertical offset in px (transform only → compositor, no layout). */
  y?: number;
  /**
   * Above-the-fold: render visible on first paint (no opacity:0 initial). Use
   * for LCP content (e.g. the hero heading) so it isn't gated behind the JS
   * reveal — that gate adds seconds of LCP "render delay" on throttled mobile.
   */
  eager?: boolean;
  className?: string;
  style?: CSSProperties;
};

/** Reveal-on-scroll wrapper. Animates only opacity + transform. */
export function Reveal({ children, as = "div", delay = 0, y = 26, eager = false, className, style }: RevealProps) {
  const reduce = useReducedMotion();
  const Comp = m[as] as typeof m.div;

  // eager (LCP) or reduced-motion → paint immediately, no hidden initial state.
  if (reduce || eager) {
    return (
      <Comp className={className} style={style}>
        {children}
      </Comp>
    );
  }

  return (
    <Comp
      className={className}
      style={style}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, ease: EASE, delay }}
    >
      {children}
    </Comp>
  );
}
